// Composables
import { createRouter, createWebHistory } from 'vue-router';

import { getLang } from '@/i18n/helpers';

import { useAuthStore } from '@/features/auth/store/auth';
import { useHouseholdStore } from '@/features/household/store/household';
import { useHouseholdInvitationStore } from '@/features/household/store/invitation';

import { authRedirectRoutes } from '@/features/auth/routes';
import { authLocaleRoutes } from '@/features/auth/routes';

import { accountRoutes } from '@/features/account/routes';
import { budgetRoutes } from '@/features/budget/routes';
import { catalogRoutes } from '@/features/catalog/routes';
import { householdRoutes } from '@/features/household/routes';
import { inventoryRoutes } from '@/features/inventory/routes';
import { mealPlanningRoutes } from '@/features/meal-planning/routes';
import { productsRoutes } from '@/features/products/routes';
import { purchaseRoutes } from '@/features/purchase/routes';
import { recipeRoutes } from '@/features/recipe/routes';
import { subscriptionRoutes } from '@/features/subscription/routes';

const routes = [
  ...authRedirectRoutes,

  // Routes publiques (auth uniquement) avec locale
  {
    path: '/:locale',
    component: () => import('@/core/layouts/auth/AuthLayout.vue'),
    children: [
      {
        path: '', 
        redirect: (to: { params: { locale?: string } }) => `/${to.params.locale}/login`
      },
      ...authLocaleRoutes,
    ]
  },

  // Routes protégées SANS locale
  {
    path: '/',
    meta: {
      requiresAuth: true,
      requiresHousehold: true
    },
    component: () => import('@/core/layouts/app/AppLayout.vue'),
    children: [
      {
        path: '',
        component: () => import('@/features/dashboard/views/Dashboard.vue'),
      },
      ...accountRoutes,
      ...budgetRoutes,
      ...catalogRoutes,
      ...householdRoutes,
      ...inventoryRoutes,
      ...mealPlanningRoutes,
      ...productsRoutes,
      ...purchaseRoutes,
      ...recipeRoutes,
      ...subscriptionRoutes,
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/core/views/SettingsHub.vue'),
      },
      {
        path: 'get-app',
        name: 'GetApp',
        component: () => import('@/core/views/GetApp.vue'),
      },
      {
        path: 'down',
        name: 'ServiceUnavailable',
        component: () => import('@/core/views/ServiceUnavailable.vue'),
        meta: { requiresHousehold: false }
      },
    ]
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Navigation guard to check household membership
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Ensure auth state is loaded
  if (authStore.loading) {
    await authStore.fetchUser()
  }
  
  const isAuthenticated = authStore.isAuthenticated

  if (!to.meta.requiresAuth && isAuthenticated && to.name !== 'Recovery') {
    return next({ path: '/' });
  }

  if (!to.meta.requiresAuth && !isAuthenticated) {
    return next();
  }

  if (to.meta.requiresAuth && !isAuthenticated) {
    const locale = to.params.locale || getLang(navigator.language)
    return next({ name: 'Login', params: { locale }, query: { redirect: to.fullPath } })
  }

  // Skip check for household pages
  if (['/household/create', '/household/invitation'].includes(to.path)) {
    return next();
  }

  // Check if route requires household membership
  if (to.meta.requiresHousehold) {
    const householdStore = useHouseholdStore();
    const householdInvitationStore = useHouseholdInvitationStore();

    try {
      const responsePendingInvitation = await householdInvitationStore.getPendingInvitation();
      if (responsePendingInvitation.status === "success" && responsePendingInvitation.data) {
        return next({ path: '/household/invitation' });
      }

      const hasHousehold = await householdStore.getHousehold();
      if (hasHousehold.status === "error") {
        // Redirect to household creation if user has no household
        return next({ path: '/household/create' });
      }
    } catch (error) {
      console.error('Error checking household membership:', error);
      // You might want to handle this error differently
      return next({ path: '/household/create' });
    }
  }

  next();
});

export default router;
